import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatDate, formatTRY } from "@/lib/api";
import { Printer, ArrowLeft, CarFront } from "lucide-react";

const STATUS_COLOR = {
  Beklemede: "bg-amber-100 text-amber-800 border-amber-300",
  "Teslim Edildi": "bg-blue-100 text-blue-800 border-blue-300",
  "Kısmi Ödeme": "bg-violet-100 text-violet-800 border-violet-300",
  "Tahsil Edildi": "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function JobPrint() {
  const { id } = useParams();
  const [j, setJ] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJ(r.data)).catch(() => setErr("İş kaydı bulunamadı"));
  }, [id]);

  useEffect(() => {
    if (j) setTimeout(() => window.print(), 500);
  }, [j]);

  if (err) return <div className="p-8 text-center text-rose-600">{err}</div>;
  if (!j) return <div className="p-8 text-center text-slate-500">Yükleniyor…</div>;

  const gross = (j.price || 0) * (1 + (j.vat_rate || 0));
  const vatAmount = (j.price || 0) * (j.vat_rate || 0);
  const remaining = Math.max(gross - (j.paid_amount || 0), 0);
  const jobTypes = (j.job_type || "").split(",").map((s) => s.trim()).filter(Boolean);
  const now = new Date().toLocaleString("tr-TR");

  return (
    <div className="bg-white min-h-screen text-slate-900">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>

      <div className="no-print bg-slate-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-50">
        <a href="/jobs" className="inline-flex items-center gap-2 text-sm hover:text-blue-300" data-testid="job-print-back">
          <ArrowLeft className="w-4 h-4" /> İş Kayıtlarına Dön
        </a>
        <button
          data-testid="job-print-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
        >
          <Printer className="w-4 h-4" /> Yazdır / PDF
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0" data-testid="job-print-doc">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-slate-900 flex items-center justify-center">
              <CarFront className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold leading-tight">Global Car Service</h1>
              <p className="text-[10px] text-slate-600 leading-tight">Kaporta · Boya · Şase Onarım</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">İş Emri / Kayıt</p>
            <p className="font-display text-lg font-bold font-mono-num mt-0.5">#{(j.id || "").slice(0, 8).toUpperCase()}</p>
            <p className="text-[10px] text-slate-600">Kayıt: {formatDate(j.date)}</p>
            <p className="text-[9px] text-slate-500">Basım: {now}</p>
          </div>
        </div>

        {/* Müşteri & Araç — dikey stack */}
        <div className="space-y-3 mb-5">
          <div className="border border-slate-200 rounded p-3 bg-slate-50 avoid-break">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Müşteri</p>
            <p className="font-semibold text-base">{j.customer_name || "-"}</p>
            {j.customer_phone && <p className="text-sm text-slate-600 mt-1">Tel: {j.customer_phone}</p>}
          </div>
          <div className="border border-slate-200 rounded p-3 bg-slate-50 avoid-break">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Araç</p>
            {j.plate && (
              <p className="inline-block px-3 py-1 bg-slate-900 text-white rounded font-mono-num font-bold text-base">
                {(j.plate || "").toUpperCase()}
              </p>
            )}
            {j.car_model && <p className="text-sm text-slate-700 mt-1.5">{j.car_model}</p>}
          </div>
        </div>

        {/* İş Türü + Durum — dikey stack */}
        <div className="space-y-3 mb-5 avoid-break">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">İş Türü</p>
            <div className="flex flex-wrap gap-1.5">
              {jobTypes.length > 0 ? jobTypes.map((t) => (
                <span key={t} className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200 text-xs font-medium">
                  {t}
                </span>
              )) : <span className="text-slate-500 text-sm">-</span>}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Durum</p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${STATUS_COLOR[j.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
              {j.status}
            </span>
          </div>
        </div>

        {/* Parça / Line Items */}
        {j.line_items && j.line_items.length > 0 && (
          <div className="mb-5 avoid-break">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Parça / Operasyon Detayı</p>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100 border-t border-b-2 border-slate-900">
                  <th className="text-left px-2 py-1.5 text-[10px] uppercase font-bold w-8">#</th>
                  <th className="text-left px-2 py-1.5 text-[10px] uppercase font-bold">Parça</th>
                  <th className="text-left px-2 py-1.5 text-[10px] uppercase font-bold w-32">Operasyon</th>
                  <th className="text-right px-2 py-1.5 text-[10px] uppercase font-bold w-28">Tutar ₺</th>
                </tr>
              </thead>
              <tbody>
                {j.line_items.map((li, i) => (
                  <tr key={li.id || i} className="border-b border-slate-200">
                    <td className="px-2 py-1.5 text-xs font-mono-num text-slate-600">{i + 1}</td>
                    <td className="px-2 py-1.5 text-xs">{li.part_label}</td>
                    <td className="px-2 py-1.5 text-xs">{li.operation}</td>
                    <td className="px-2 py-1.5 text-xs text-right font-mono-num font-semibold">
                      {formatTRY(li.price || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Açıklama */}
        {j.description && (
          <div className="mb-5 avoid-break">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Açıklama</p>
            <p className="text-sm text-slate-800 leading-relaxed border border-slate-200 rounded p-2 bg-slate-50 whitespace-pre-wrap">
              {j.description}
            </p>
          </div>
        )}

        {/* Toplam & Ödeme */}
        <div className="flex justify-end mb-6 avoid-break">
          <div className="w-96 text-sm font-mono-num">
            <div className="flex justify-between py-1 border-b border-slate-200">
              <span className="text-slate-600">Ara Toplam</span>
              <span className="font-semibold">{formatTRY(j.price || 0)}</span>
            </div>
            {(j.vat_rate || 0) > 0 && (
              <div className="flex justify-between py-1 border-b border-slate-200">
                <span className="text-slate-600">KDV ({(j.vat_rate * 100).toFixed(0)}%)</span>
                <span>{formatTRY(vatAmount)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 border-b-2 border-slate-900 text-base font-bold">
              <span>GENEL TOPLAM</span>
              <span>{formatTRY(gross)}</span>
            </div>
            {j.status === "Kısmi Ödeme" && (
              <>
                <div className="flex justify-between py-1 text-emerald-700">
                  <span>Tahsil Edilen</span>
                  <span className="font-semibold">{formatTRY(j.paid_amount || 0)}</span>
                </div>
                <div className="flex justify-between py-1 text-rose-700 border-t border-slate-200">
                  <span className="font-bold">KALAN BORÇ</span>
                  <span className="font-bold">{formatTRY(remaining)}</span>
                </div>
              </>
            )}
            {j.status === "Tahsil Edildi" && (
              <div className="flex justify-between py-1 text-emerald-700">
                <span className="font-bold">✓ TAM TAHSİL</span>
                <span className="font-bold">{formatTRY(j.paid_amount || gross)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tarihler — dikey liste */}
        {(j.delivered_date || j.collected_date || j.expected_delivery) && (
          <div className="mb-6 text-xs avoid-break border-t border-slate-200 pt-3 space-y-1.5">
            {j.expected_delivery && (
              <div className="flex justify-between">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tahmini Teslim</span>
                <span className="font-mono-num font-semibold">{formatDate(j.expected_delivery)}</span>
              </div>
            )}
            {j.delivered_date && (
              <div className="flex justify-between">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Teslim Tarihi</span>
                <span className="font-mono-num font-semibold">{formatDate(j.delivered_date)}</span>
              </div>
            )}
            {j.collected_date && (
              <div className="flex justify-between">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tahsil Tarihi</span>
                <span className="font-mono-num font-semibold">{formatDate(j.collected_date)}</span>
              </div>
            )}
          </div>
        )}

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-16 mt-10 pt-4 avoid-break">
          <div className="text-center border-t border-slate-400 pt-2">
            <p className="text-[10px] text-slate-600">Müşteri İmza</p>
          </div>
          <div className="text-center border-t border-slate-400 pt-2">
            <p className="text-[10px] text-slate-600">Yetkili İmza · Kaşe</p>
          </div>
        </div>

        <p className="text-center text-[9px] text-slate-500 mt-6">
          Global Car Service · Bu belge bilgi amaçlıdır, resmi fatura değildir.
        </p>
      </div>
    </div>
  );
}
